import React, { useState, useEffect } from 'react';
import { formatGermanTime } from '../helpers/germanTime';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  fetchPlxOrderDetails,
  PlxOrderDetails,
  fetchCampaignAttribution,
  CampaignAttribution,
} from '../helpers/plxOrdersData';
import { useNavigate } from 'react-router-dom';
import { formatGermanCurrency, formatLifetime } from '../helpers/formatFuncs';
import { formatNumber } from '../helpers/numberUtils';

interface OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  ordernumber: string | null;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ open, onClose, ordernumber }) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState<PlxOrderDetails | null>(null);
  const [attribution, setAttribution] = useState<CampaignAttribution | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && ordernumber) {
      setLoading(true);
      setError(null);
      setAttribution(null);

      // Fetch order details and campaign attribution in parallel
      Promise.all([fetchPlxOrderDetails(ordernumber), fetchCampaignAttribution(ordernumber)])
        .then(([orderData, attributionData]) => {
          setDetails(orderData);
          setAttribution(attributionData);
        })
        .catch((err) => {
          console.error('Error fetching order details:', err);
          setError('Die Bestellung konnte nicht geladen werden.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, ordernumber]);

  if (!open) return null;

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'V':
        return 'error';
      case 'L':
        return 'success';
      case 'W':
        return 'warning';
      case 'B':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string | null) => {
    const labels: Record<string, string> = {
      V: 'V - Verweigert',
      L: 'L - Laufend',
      W: 'W - Widerrufen',
      B: 'B - Beendet',
    };
    return labels[status || ''] || status || '-';
  };

  const getSubscriptionTypeColor = (type: string | null) => {
    switch (type) {
      case 'Testphase':
        return 'info';
      case 'Bezahlt':
        return 'success';
      case 'Upsell':
        return 'secondary';
      case 'Einmalfaktura':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSubscriptionTypeEmoji = (type: string | null) => {
    return type === 'Einmalfaktura' ? '💶' : type === 'Upsell' ? '📅' : '📊';
  };

  // created_on / sent_at sind Berlin-Wallclock (UTC-getaggte Bytes) — plain new Date()
  // + toLocaleString im Berlin-Browser zeigte +2h.
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatGermanTime(dateString, 'DD.MM.YYYY, HH:mm');
  };

  const formatDateOnly = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatGermanTime(dateString, 'D.M.YYYY');
  };

  const getMappingMethodLabel = (method: string) => {
    const labels: Record<
      string,
      { label: string; color: 'success' | 'primary' | 'info' | 'warning' | 'default' }
    > = {
      rid: { label: 'RID (direkt)', color: 'success' },
      banner_webinar_id: { label: 'Banner-ID (direkt)', color: 'success' },
      email_banner: { label: 'E-Mail + Banner', color: 'primary' },
      email_doi_url: { label: 'E-Mail + DOI-URL', color: 'info' },
      email_affiliate: { label: 'E-Mail + Affiliate', color: 'info' },
      unmapped: { label: 'Nicht zugeordnet', color: 'warning' },
    };
    return labels[method] || { label: method, color: 'default' };
  };

  const handleNavigateToEvaluation = (url: string) => {
    onClose();
    navigate(url);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>Bestelldetails: {ordernumber || ''}</DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box p={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && details && (
          <Box>
            {/* Customer Information Section */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight="600">
                  👤 Kundeninformationen
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body2" mb={1}>
                    <strong>Name:</strong> {details.order.gender || ''} {details.order.titel || ''}{' '}
                    {details.order.vorname || ''} {details.order.nachname || ''}
                  </Typography>
                  {details.order.firma && (
                    <Typography variant="body2" mb={1}>
                      <strong>Firma:</strong> {details.order.firma}
                    </Typography>
                  )}
                  <Typography variant="body2" mb={1}>
                    <strong>E-Mail:</strong> {details.order.email || '-'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Adresse:</strong> {details.order.strasse || ''},{' '}
                    {details.order.strasse_postleitzahl_neu || ''} {details.order.ort || ''}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Land:</strong> {details.order.countryisocode || '-'}
                  </Typography>
                  {details.order.postofficebox && (
                    <Typography variant="body2" mb={1}>
                      <strong>Postfach:</strong> {details.order.postofficebox}
                    </Typography>
                  )}
                  <Typography variant="body2" mb={1}>
                    <strong>Kunden-ID:</strong> {details.order.kunde || '-'}
                  </Typography>
                  {details.order.vatid && (
                    <Typography variant="body2" mb={1}>
                      <strong>USt-IdNr.:</strong> {details.order.vatid}
                    </Typography>
                  )}
                  <Typography variant="body2" mb={1}>
                    <strong>Vertrags-Seq.:</strong> {details.order.vertrags_seqnr || '-'}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Subscription Details Section */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight="600">
                  📋 Abo-Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body2" mb={1}>
                    <strong>Status:</strong>{' '}
                    <Chip
                      label={getStatusLabel(details.order.vertrag_sts)}
                      color={getStatusColor(details.order.vertrag_sts)}
                      size="small"
                    />
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Abo-Typ:</strong>{' '}
                    {details.order.subscription_type ? (
                      <Chip
                        label={`${getSubscriptionTypeEmoji(details.order.subscription_type)} ${details.order.subscription_type}`}
                        color={getSubscriptionTypeColor(details.order.subscription_type)}
                        size="small"
                      />
                    ) : (
                      '-'
                    )}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Bestelldatum:</strong> {formatDate(details.order.created_on)} (
                    {details.order.days_since_order} Tage her)
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Bestellnummer:</strong> {details.order.ordernumber}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Produkt:</strong> {details.order.product_name} (
                    {details.order.artikel || '-'})
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Testphase:</strong> {details.order.trial_days} Tage
                  </Typography>
                  {details.order.kuendigungsdatum && (
                    <Typography variant="body2" mb={1}>
                      <strong>Kündigungsdatum:</strong>{' '}
                      {formatDateOnly(details.order.kuendigungsdatum)}
                    </Typography>
                  )}
                  {details.order.aboende_datum && (
                    <Typography variant="body2" mb={1}>
                      <strong>Abo-Ende:</strong> {formatDateOnly(details.order.aboende_datum)}
                    </Typography>
                  )}
                  <Typography variant="body2" mb={1}>
                    <strong>Abo-Laufzeit:</strong>{' '}
                    {formatLifetime(details.order.subscription_lifetime_days)}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Status (intern):</strong> {details.order.status}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Pricing & Revenue Section */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight="600">
                  💰 Preise & Umsatz
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body2" mb={1}>
                    <strong>Quartalspreis:</strong> {formatNumber(details.order.quarterly_price, 2)}
                    €
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Angezeigter Preis:</strong>{' '}
                    {formatNumber(details.order.display_price, 2)}€
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Ist Upsell (Jahresabo):</strong>{' '}
                    {details.order.is_upsell ? 'Ja' : 'Nein'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Ist Einmalfaktura:</strong> {details.order.is_one_time ? 'Ja' : 'Nein'}
                  </Typography>
                  <Box mt={2} mb={2} sx={{ borderTop: '1px solid #ddd', paddingTop: 2 }}>
                    {/*
                      Beide Werte stammen aus plx_order_metrics und sind MODELLIERT, nicht
                      gemessen: invoices_paid = CEIL((Laufzeit - Testphase) / 90) bzw. Jahre bei
                      Jahresabo, total_revenue_generated = Quartalspreis x invoices_paid.
                      `zahlung_brutto` kommt in keiner der beiden Formeln vor (plxOrderMetrics.ts).
                      Das tatsaechlich eingegangene Geld steht in der Zahlungsuebersicht darunter.
                    */}
                    <Typography variant="body2" mb={1}>
                      <strong>Theoretischer Umsatz (Modell):</strong>{' '}
                      {formatGermanCurrency(
                        details.order.total_revenue_generated,
                        details.order.invoices_paid,
                      )}
                      €
                    </Typography>
                    <Typography variant="body2" mb={1}>
                      <strong>Modellierte Abrechnungszyklen:</strong>{' '}
                      {details.order.invoices_paid || 0}
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Payment Breakdown Section */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight="600">
                  💳 Zahlungsübersicht
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {details.order.has_actual_data ? (
                    <>
                      <Box mb={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography
                          variant="body2"
                          mb={1}
                          sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                          <strong>Tatsächlicher Umsatz:</strong>{' '}
                          {formatGermanCurrency(details.order.actual_revenue || 0, null)}€
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          <strong>Theoretischer Umsatz:</strong>{' '}
                          {formatGermanCurrency(details.order.total_revenue_generated || 0, null)}€
                        </Typography>
                        {details.order.revenue_variance !== null &&
                          details.order.revenue_variance !== 0 && (
                            <Typography
                              variant="body2"
                              mb={1}
                              sx={{
                                color:
                                  (details.order.revenue_variance || 0) > 0
                                    ? 'error.main'
                                    : 'success.main',
                                fontWeight: 600,
                              }}
                            >
                              <strong>Differenz:</strong>{' '}
                              {formatGermanCurrency(
                                Math.abs(details.order.revenue_variance || 0),
                                null,
                              )}
                              €{(details.order.revenue_variance || 0) > 0 ? ' weniger' : ' mehr'}{' '}
                              als erwartet
                            </Typography>
                          )}
                      </Box>

                      {/* Bezahlt-Schwelle wie im Backend (plxOrderRules.unpaidSql): ein Betrag bis
                          1 € ist die Testphasen-Rechnung und keine Zahlung. */}
                      {(details.order.zahlung_brutto ?? 0) <= 1 && (
                        <Box
                          mb={2}
                          p={2}
                          sx={{
                            backgroundColor: '#fff3e0',
                            borderRadius: 1,
                            border: '1px solid #ff9800',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: 'warning.dark', fontWeight: 600 }}
                          >
                            ⚠️ Keine Zahlung über 1 € erhalten (eine Testphasen-Rechnung bis 1 €
                            zählt nicht als Zahlung)
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="subtitle2" mb={1} mt={2} sx={{ fontWeight: 600 }}>
                        Detaillierte Zahlungsübersicht:
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Rechnungsstellung (brutto):</strong>{' '}
                        {formatGermanCurrency(details.order.umsatz_brutto || 0, null)}€
                      </Typography>
                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ color: 'success.main', fontWeight: 600 }}
                      >
                        <strong>✓ Eingehende Zahlungen:</strong>{' '}
                        {formatGermanCurrency(details.order.zahlung_brutto || 0, null)}€
                      </Typography>

                      {((details.order.storno_brutto || 0) > 0 ||
                        (details.order.ausbuchung_brutto || 0) > 0 ||
                        (details.order.rueckueberweisung_brutto || 0) > 0 ||
                        (details.order.ruecklastschrift_brutto || 0) > 0) && (
                        <>
                          <Typography
                            variant="subtitle2"
                            mb={1}
                            mt={2}
                            sx={{ fontWeight: 600, color: 'error.main' }}
                          >
                            Zahlungsabzüge:
                          </Typography>
                          {(details.order.storno_brutto || 0) > 0 && (
                            <Typography variant="body2" mb={1} sx={{ color: 'error.main' }}>
                              <strong>✗ Stornierungen:</strong> -
                              {formatGermanCurrency(details.order.storno_brutto || 0, null)}€
                            </Typography>
                          )}
                          {(details.order.ausbuchung_brutto || 0) > 0 && (
                            <Typography variant="body2" mb={1} sx={{ color: 'error.main' }}>
                              <strong>✗ Ausbuchungen:</strong> -
                              {formatGermanCurrency(details.order.ausbuchung_brutto || 0, null)}€
                            </Typography>
                          )}
                          {(details.order.rueckueberweisung_brutto || 0) > 0 && (
                            <Typography variant="body2" mb={1} sx={{ color: 'error.main' }}>
                              <strong>✗ Rückerstattungen:</strong> -
                              {formatGermanCurrency(
                                details.order.rueckueberweisung_brutto || 0,
                                null,
                              )}
                              €
                            </Typography>
                          )}
                          {(details.order.ruecklastschrift_brutto || 0) > 0 && (
                            <Typography variant="body2" mb={1} sx={{ color: 'error.main' }}>
                              <strong>✗ Rücklastschriften:</strong> -
                              {formatGermanCurrency(
                                details.order.ruecklastschrift_brutto || 0,
                                null,
                              )}
                              €
                            </Typography>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <Box p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Keine aktuellen Zahlungsdaten verfügbar. Angezeigte Umsätze basieren auf
                        theoretischen Berechnungen.
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Theoretischer Umsatz:</strong>{' '}
                        {formatGermanCurrency(details.order.total_revenue_generated || 0, null)}€
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Payment History Section */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight="600">
                  📅 Zahlungsverlauf
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {details.paymentEvents && details.paymentEvents.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>#</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Datum</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Betrag</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Typ</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Status</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {details.paymentEvents.map((payment, index) => (
                          <TableRow key={index}>
                            <TableCell>{payment.payment_number}</TableCell>
                            <TableCell>{formatDateOnly(payment.payment_date)}</TableCell>
                            <TableCell align="right">
                              {formatNumber(payment.payment_amount, 2)}€
                            </TableCell>
                            <TableCell>{payment.subscription_type}</TableCell>
                            <TableCell>
                              {payment.actually_paid ? (
                                <Chip label="✓ Bezahlt" color="success" size="small" />
                              ) : payment.is_past ? (
                                <Chip label="✗ Unbezahlt" color="error" size="small" />
                              ) : (
                                <Chip label="⏱ Ausstehend" color="default" size="small" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Keine Zahlungsdaten verfügbar
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Marketing & Tracking Section */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight="600">
                  📊 Marketing & Tracking
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body2" mb={1}>
                    <strong>Kampagne (WKZ):</strong> {details.order.werbeaktion_wbkz || '-'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Banner:</strong> {details.order.banner || '-'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Offer ID:</strong> {details.order.offerid || '-'}
                  </Typography>
                  {details.order.offerurl && (
                    <Typography variant="body2" mb={1}>
                      <strong>Offer URL:</strong>{' '}
                      <a href={details.order.offerurl} target="_blank" rel="noopener noreferrer">
                        {details.order.offerurl}
                      </a>
                    </Typography>
                  )}
                  <Typography variant="body2" mb={1}>
                    <strong>Erstellt von:</strong> {details.order.created_by || '-'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Mandator ID:</strong> {details.order.mandatorid}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Campaign Attribution Section */}
            {attribution && attribution.type !== 'none' && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="600">
                    📣 Kampagnen-Zuordnung
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {attribution.type === 'webinar' && attribution.webinar && (
                      <>
                        <Typography variant="body2" mb={1}>
                          <strong>Typ:</strong>{' '}
                          <Chip label="Webinar" color="primary" size="small" />
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          <strong>Webinar:</strong> {attribution.webinar.title}
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          <strong>Webinar-ID:</strong> {attribution.webinar.webinar_id}
                        </Typography>
                        {attribution.webinar.start_date && (
                          <Typography variant="body2" mb={1}>
                            <strong>Datum:</strong> {formatDateOnly(attribution.webinar.start_date)}
                          </Typography>
                        )}
                        <Typography variant="body2" mb={1}>
                          <strong>Zuordnung:</strong>{' '}
                          <Chip
                            label={getMappingMethodLabel(attribution.webinar.mapping_method).label}
                            color={getMappingMethodLabel(attribution.webinar.mapping_method).color}
                            size="small"
                          />
                        </Typography>
                        <Box mt={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() =>
                              handleNavigateToEvaluation(attribution.webinar!.evaluation_url)
                            }
                          >
                            Zur Webinar-Auswertung
                          </Button>
                        </Box>
                      </>
                    )}

                    {attribution.type === 'plx_mailing' && attribution.plx_mailing && (
                      <>
                        <Typography variant="body2" mb={1}>
                          <strong>Typ:</strong>{' '}
                          <Chip label="PLX Direkt-Mailing" color="secondary" size="small" />
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          <strong>Kampagne:</strong> {attribution.plx_mailing.campaign_name}
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          <strong>Produkt:</strong> {attribution.plx_mailing.product_name}
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          <strong>Mailing Nr.:</strong> {attribution.plx_mailing.mailing_number}
                        </Typography>
                        {attribution.plx_mailing.email_subject && (
                          <Typography variant="body2" mb={1}>
                            <strong>Betreff:</strong> {attribution.plx_mailing.email_subject}
                          </Typography>
                        )}
                        {attribution.plx_mailing.sent_at && (
                          <Typography variant="body2" mb={1}>
                            <strong>Gesendet:</strong> {formatDate(attribution.plx_mailing.sent_at)}
                          </Typography>
                        )}
                        <Box mt={2}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() =>
                              handleNavigateToEvaluation(attribution.plx_mailing!.evaluation_url)
                            }
                          >
                            Zur Kampagnen-Auswertung
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsModal;
